import { useCallback, useEffect, useRef, useState } from "react";
import { adminReqConfig, api, baseURL } from "../../include/api";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useNotification } from "../../Toast/ToastProvider";
import { ERROR, SUCCESS } from "../../include/constant";
import { ChevronDown, X } from "react-feather";
import Papa from "papaparse";
import "../../styles/CategoryProductList.css";
import AdminHeader from "./Header";
import { formatShortMonthDateYearWithTime, getAdmin } from "../../include/function";

const ListDocument = () => {
  const navigate = useNavigate();
  const [fetchLoading, setFetchLoading] = useState(true);
  const [customerList, setCustomerList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [selectedData, setSelectedData] = useState([]);
  const toast = useNotification();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditingCreditLimit, setIsEditingCreditLimit] = useState(false);
  const [isEditingCreditDays, setIsEditingCreditDays] = useState(false);
  const [creditBal, setCreditBal] = useState("");
  const [creditLimit, setCreditLimit] = useState("");
  const [customerDetail, setCustomerDetail] = useState([]);
  const [searchParams] = useSearchParams();
  const initialQuery = searchParams.get("q") || "";
  const [searchQuery, setSearchQuery] = useState(initialQuery);

  useEffect(() => {
    const q = searchParams.get("q") || "";
    setSearchQuery(q);
  }, [searchParams]);
  const [filterValues, setFilterValues] = useState({
    proofStatus: "",
    customerType: "",
  });
  const user = getAdmin();
  const [productPermissions, setProductPermissions] = useState({
    add: false,
    view: false,
    edit: false,
    delete: false,
  });
  useEffect(() => {
    const fetchPermissions = async () => {
      if (!user?.roleId) return;

      try {
        const res = await api.get(
          `/role_permission/?roleId=${user.roleId}`,
          adminReqConfig()
        );
        if (res.status === 200 && Array.isArray(res.data.data)) {
          // Find permission entry where pageTitle === "Product"
          const productPerm = res.data.data.find(
            (p) => p.pageTitle === "Document Verify"
          );

          if (productPerm) {
            setProductPermissions({
              add: productPerm.add === "true" || productPerm.add === 1,
              view: productPerm.view === "true" || productPerm.view === 1,
              edit: productPerm.edit === "true" || productPerm.edit === 1,
              delete: productPerm.delete === "true" || productPerm.delete === 1,
            });
          }
        }
      } catch (err) {
        console.warn("Failed to fetch product permissions", err);
      }
    };

    fetchPermissions();
  }, [user?.roleId]);
  const PROOFSTATUS_OPTIONS = [
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
    { value: "pending", label: "Pending" },
  ];

  const getCustomer = (filters = {}) => {
    setFetchLoading(true);
    const params = new URLSearchParams();
    if (filters.customerType) params.set("customerType", filters.customerType);
    if (filters.proofStatus) params.set("proofStatus", filters.proofStatus);
    const url = `/customer/?${params.toString()}`;
    api
      .get(url, adminReqConfig())
      .then((response) => {
        if (response.status === 200) {
          const normalizedData = response.data.data;
          setCustomerList(normalizedData);
          setFilteredData(normalizedData);
        }
      })
      .catch((error) => {
        console.error("Failed to fetch orders:", error);
        setCustomerList([]);
        setFilteredData([]);
      })
      .finally(() => setFetchLoading(false));
  };

  useEffect(() => {
    getCustomer();
  }, []);

  const getCustomerDetail = useCallback((id) => {
    setFetchLoading(true);
    api
      .get(`/customer/?id=${id}`, adminReqConfig())
      .then((response) => {
        if (response.status === 200) {
          const customer = response.data.data[0] || null;
          setCustomerDetail(customer);
          if (customer) {
            setCreditBal(customer.creditBal || "");
            setCreditLimit(customer.creditLimit || "");
          }
        }
      })
      .catch((error) => {
        toast({ type: "error", message: "Failed to load customer detail." });
        setCustomerDetail([]);
      })
      .finally(() => {
        setFetchLoading(false);
      });
  }, []);

  // 🔥 REAL-TIME SEARCH + FILTERS
  useEffect(() => {
    let result = [...customerList];

    // Apply dropdown filters first
    if (filterValues.customerType) {
      result = result.filter(
        (c) => c.customerType === filterValues.customerType
      );
    }
    if (filterValues.proofStatus) {
      result = result.filter((c) => c.proofStatus === filterValues.proofStatus);
    }

    // Apply search query (real-time) on: name, customerType, proofStatus
    if (searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase();
      result = result.filter(
        (customer) =>
          customer.name?.toLowerCase().includes(query) ||
          customer.customerType?.toLowerCase().includes(query) ||
          PROOFSTATUS_OPTIONS.some(
            (opt) =>
              opt.value === customer.proofStatus &&
              opt.label.toLowerCase().includes(query)
          )
      );
    }

    // Always show only customers with proofs
    result = result.filter(
      (customer) => customer.proof && customer.proof.length > 0
    );

    setFilteredData(result);
    setCurrentPage(1);
  }, [searchQuery, filterValues, customerList, searchParams]);

  // Pagination Logic
  let currentItems = [];
  let totalPage = 1;
  let showingFrom = 0;
  let showingTo = 0;
  if (itemsPerPage === "all") {
    currentItems = filteredData;
    showingFrom = filteredData.length > 0 ? 1 : 0;
    showingTo = filteredData.length;
  } else {
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
    totalPage = Math.ceil(filteredData.length / itemsPerPage);
    showingFrom = indexOfFirstItem + 1;
    showingTo = Math.min(indexOfLastItem, filteredData.length);
  }
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // ... [Rest of handlers: select, CSV, edit, view, delete] remain unchanged ...

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = currentItems.map((p) => p.id);
      setSelectedData((prev) => [...new Set([...prev, ...allIds])]);
    } else {
      const idsToRemove = currentItems.map((p) => p.id);
      setSelectedData((prev) => prev.filter((id) => !idsToRemove.includes(id)));
    }
  };
  const handleSelectOne = (id) => {
    setSelectedData((prev) =>
      prev.includes(id) ? prev.filter((pid) => pid !== id) : [...prev, id]
    );
  };
const handleDownloadCSV = (exportScope = "current") => {
  let dataToExport = [];

  // ✅ PRIORITY 1: If items selected → ALWAYS export selected (overrides everything)
  if (selectedData.length > 0) {
    dataToExport = customerList.filter((p) => selectedData.includes(p.id));
  } 
  // ✅ PRIORITY 2: If "Export All" clicked → export entire dataset
  else if (exportScope === "all") {
    dataToExport = customerList;
  } 
  // ✅ PRIORITY 3: Default → export current page items only
  else {
    dataToExport = currentItems;
  }

  if (dataToExport.length === 0) {
    toast({ type: "error", message: "No customer data to download" });
    return;
  }

  const csvRows = dataToExport.map((customer, index) => ({
    "S.No": index + 1,
    "Cus ID": customer.id || "-",
    "Customer Name": customer.name || "-",
    "Customer Type": customer.customerType || "N/A",
    PhoneNo: customer.phoneNumber || "N/A",
    Address: [customer.address1, customer.address2].filter(Boolean).join(", ") || "N/A",
    "Total Transaction": customer.totalTransaction || "-",
  }));

  const columns = [
    "S.No",
    "Cus ID",
    "Customer Name",
    "Customer Type",
    "PhoneNo",
    "Address",
    "Total Transaction",
  ];

  // ✅ CORRECT SYNTAX: fields + data keys (NO parsing error)
  const csv = Papa.unparse({
    fields: columns,
    data: csvRows,
  });

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  
  // ✅ Dynamic filename based on export type
  const fileName = selectedData.length > 0 
    ? `Document_Selected_${selectedData.length}.csv`
    : exportScope === "all" 
      ? "Document_All.csv" 
      : `Document_Page${currentPage}.csv`;
    
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
  const handleEdit = (custId) => {
    navigate(`/admin/document/documentVerify/${custId}`);
  };
  const handleView = (custId) => {
    getCustomerDetail(custId);
    setIsModalOpen(true);
  };
  const handleDelete = (custId) => {
    if (window.confirm("Are you sure you want to delete this customer?")) {
      api
        .delete(`/customer/?id=${custId}`, adminReqConfig())
        .then((response) => {
          if (response) {
            toast({ type: SUCCESS, message: response.data.message });
            setCustomerList((prev) => prev.filter((p) => p.id !== custId));
            setFilteredData((prev) => prev.filter((p) => p.id !== custId));
          }
        })
        .catch((error) => {
          toast({ type: ERROR, message: "Failed to delete customer" });
          console.error("Delete error:", error);
        });
    }
  };
  const handleSaveCreditAndLimit = (id) => {
    const payload = {
      creditBal,
      creditLimit,
    };
    setFetchLoading(true);
    api
      .put(
        `/customer/?id=${id}&flag=changeAdminLimit`,
        payload,
        adminReqConfig()
      )
      .then((response) => {
        if (response.status === 200) {
          toast({
            type: SUCCESS,
            message: "Credit details added successfully!",
          });
          getCustomerDetail(id);
        }
      })
      .catch(() => {
        toast({ type: ERROR, message: "Failed to update credit details!" });
      })
      .finally(() => {
        setFetchLoading(false);
      });
  };
  const clearSaveCreditAndLimit = () => {
    setCreditBal("");
    setCreditLimit("");
  };

  // Close filter panel on outside click
  const filterWrapperRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isFilterPanelOpen &&
        filterWrapperRef.current &&
        !filterWrapperRef.current.contains(event.target)
      ) {
        setIsFilterPanelOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isFilterPanelOpen]);

  const applyFilters = () => {
    setIsFilterPanelOpen(false);
  };

  const clearFilters = () => {
    setFilterValues({ proofStatus: "", customerType: "" });
    setSearchQuery("");
    setIsFilterPanelOpen(false);
  };

  const formatCustomerId = (customer) => {
    if (!customer?.createTimestamp || !customer?.id) return "CUS--";

    // Extract last 2 digits of year
    const year = new Date(customer.createTimestamp).getFullYear();
    const yy = String(year).slice(-2); // e.g., 2025 → "25"

    // Zero-pad order.id to 4 digits
    const paddedId = String(customer.id).padStart(4, "0"); // e.g., 7 → "0007"

    return `CUS${yy}-${paddedId}`;
  };

  return (
    <>
      <AdminHeader title={"DOCUMENT LIST"}>
        <div className="content-wrapper p-0">
          <section
            style={{
              backgroundColor: "#F1F4F9",
              padding: "30px",
              marginTop: "70px",
              marginBottom: "40px",
              minHeight: "80vh",
            }}
          >
            {fetchLoading ? (
              <p>Loading products...</p>
            ) : (
              <>
                <div className="rounded-5 bg-white py-24 ">
                  <div className="row align-items-center px-24 py-10 mb-3">
                    <div className="col-auto">
                      <h6 className="mb-0">All Document List</h6>
                    </div>
                    <div className="col d-flex align-items-center">
                      {/*  Updated: Added proper search input */}
                      <div
                        className="filter-wrapper"
                        style={{ position: "relative", width: "500px" }}
                        ref={filterWrapperRef}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            border: "1px solid #c6d6f5",
                            borderRadius: "8px",
                            backgroundColor: "#fff",
                            width: "100%",
                          }}
                        >
                          <img
                            src="/assets/images/search-icon.png"
                            style={{ width: "18px", marginLeft: "8px" }}
                          />
                          <input
                            type="text"
                            placeholder="Search by name, type, or status..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onFocus={() => setIsFilterPanelOpen(true)}
                            style={{
                              border: "none",
                              outline: "none",
                              padding: "8px 8px",
                              width: "100%",
                              fontSize: "14px",
                              color: "#333",
                              backgroundColor: "transparent",
                            }}
                          />
                          {searchQuery && (
                            <X
                              size={24}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSearchQuery("");
                                setCurrentPage(1);
                                setIsFilterPanelOpen(false);
                              }}
                              style={{
                                position: "absolute",
                                right: "40px",
                                cursor: "pointer",
                                color: "#999",
                                padding: "4px",
                                zIndex: 10,
                              }}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.color = "#666")
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.color = "#999")
                              }
                            />
                          )}
                          <ChevronDown
                            size={16}
                            style={{
                              marginRight: "8px",
                              color: "#999",
                              cursor: "pointer",
                              transform: isFilterPanelOpen
                                ? "rotate(180deg)"
                                : "rotate(0deg)",
                              transition: "transform 0.2s",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setIsFilterPanelOpen(!isFilterPanelOpen);
                            }}
                          />
                        </div>
                        {isFilterPanelOpen && (
                          <FilterPanel
                            filterValues={filterValues}
                            onFilterChange={(key, value) =>
                              setFilterValues((prev) => ({
                                ...prev,
                                [key]: value,
                              }))
                            }
                            onApply={applyFilters}
                            onClear={clearFilters}
                          />
                        )}
                      </div>
                    </div>
                    <div className="d-flex col-auto">
                      <CustomDropdown handleDownloadCSV={handleDownloadCSV} />
                    </div>
                  </div>

                  {/* Table */}
                  <div className="table-responsive mt-24 product-table">
                    <table className="table table-striped custom-striped-table-product-list align-middle">
                      <thead className="">
                        <tr>
                          <th scope="col" style={{ width: "50px" }}>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              style={{ borderColor: "#c5d5f5" }}
                              onChange={handleSelectAll}
                              checked={
                                currentItems.length > 0 &&
                                currentItems.every((p) =>
                                  selectedData.includes(p.id),
                                )
                              }
                            />
                          </th>
                          <th
                            scope="col"
                            style={{
                              fontSize: "15px",
                              textWrap: "nowrap",
                            }}
                          >
                            Cust.ID
                          </th>
                          <th
                            scope="col"
                            style={{ fontSize: "15px", textWrap: "nowrap" }}
                          >
                            Customer Name
                          </th>
                          <th
                            scope="col"
                            style={{ fontSize: "15px", textWrap: "nowrap" }}
                          >
                            Customer Type
                          </th>
                          <th
                            scope="col"
                            style={{ fontSize: "15px", textWrap: "nowrap" }}
                          >
                            Phone Number
                          </th>
                          <th
                            scope="col"
                            style={{ fontSize: "15px", textWrap: "nowrap" }}
                          >
                            Address
                          </th>
                          <th
                            scope="col"
                            style={{ fontSize: "15px", textWrap: "nowrap" }}
                          >
                            Date & Time
                          </th>
                          <th
                            scope="col"
                            style={{ fontSize: "15px", textWrap: "nowrap" }}
                          >
                            Status
                          </th>

                          <th
                            scope="col"
                            style={{ fontSize: "15px", textWrap: "nowrap" }}
                          >
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentItems.length > 0 ? (
                          currentItems.map((customer) => {
                            return (
                              <tr key={customer.id}>
                                <td>
                                  <input
                                    type="checkbox"
                                    style={{
                                      borderColor: "#c5d5f5",
                                    }}
                                    className="form-check-input"
                                    checked={selectedData.includes(customer.id)}
                                    onChange={() =>
                                      handleSelectOne(customer.id)
                                    }
                                  />
                                </td>
                                <td
                                  style={{
                                    textWrap: "nowrap",
                                    overflow: "hidden",
                                  }}
                                >
                                  {formatCustomerId(customer)}
                                </td>
                                <td>{customer.name || "N/A"}</td>
                                <td>{customer.customerType || "N/A"}</td>
                                <td>{customer.phoneNumber || "N/A"}</td>
                                <td>
                                  {customer.address1 || "N/A"},
                                  {customer.address2}
                                </td>
                                <td>
                                  {formatShortMonthDateYearWithTime(
                                    customer.createTimestamp,
                                  )}
                                </td>
                                <td>
                                  {customer.proofStatus && (
                                    <span
                                      style={{
                                        padding: "6px 12px",
                                        border: `1px solid ${
                                          getStatusColors(customer.proofStatus)
                                            .color
                                        }`,
                                        borderRadius: "6px",
                                        backgroundColor: getStatusColors(
                                          customer.proofStatus,
                                        ).bg,
                                        fontWeight: "500",
                                        color: getStatusColors(
                                          customer.proofStatus,
                                        ).color,
                                        display: "inline-block",
                                        fontSize: "14px",
                                        textWrap: "nowrap",
                                      }}
                                    >
                                      {PROOFSTATUS_OPTIONS.find(
                                        (opt) =>
                                          opt.value === customer.proofStatus,
                                      )?.label || customer.proofStatus}
                                    </span>
                                  )}
                                </td>
                                <td style={{ position: "relative" }}>
                                  <div className="d-flex gap-1">
                                    {productPermissions.view && (
                                      <button
                                        className="list-doc-action-btn"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          handleView(customer.id);
                                        }}
                                      >
                                        <img
                                          src="/assets/images/view-icon.png"
                                          alt="View"
                                          style={{
                                            width: "16px",
                                            height: "16px",
                                            objectFit: "contain",
                                          }}
                                          onError={(e) =>
                                            (e.target.style.display = "none")
                                          }
                                        />
                                      </button>
                                    )}
                                    {productPermissions.edit && (
                                      <button
                                        className="list-doc-action-btn"
                                        onClick={() => handleEdit(customer.id)}
                                      >
                                        <img
                                          src="/assets/images/edit-icon.png"
                                          alt="Delete"
                                          style={{
                                            width: "16px",
                                            height: "16px",
                                            objectFit: "contain",
                                          }}
                                          onError={(e) =>
                                            (e.target.style.display = "none")
                                          }
                                        />
                                      </button>
                                    )}
                                    {productPermissions.delete && (
                                      <button
                                        className="list-doc-action-btn"
                                        onClick={() =>
                                          handleDelete(customer.id)
                                        }
                                      >
                                        <img
                                          src="/assets/images/delete-icon.png"
                                          alt="Delete"
                                          style={{
                                            width: "16px",
                                            height: "16px",
                                            objectFit: "contain",
                                          }}
                                          onError={(e) =>
                                            (e.target.style.display = "none")
                                          }
                                        />
                                      </button>
                                    )}
                                  </div>
                                </td>{" "}
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="8" className="text-center py-4">
                              No Documents found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="d-flex justify-content-between align-items-center mt-16 p-16">
                    <div className="d-flex align-items-center">
                      <select
                        className="form-select d-inline-block w-auto"
                        value={itemsPerPage}
                        onChange={(e) => {
                          const val = e.target.value;
                          setItemsPerPage(
                            val === "all" ? "all" : parseInt(val),
                          );
                          setCurrentPage(1);
                        }}
                        style={{
                          height: "34px",
                          fontSize: "14px",
                          minWidth: "70px",
                        }}
                      >
                        <option value="20">20</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                        <option value="all">All</option>
                      </select>
                      <span className="ms-2">
                        Showing {showingFrom} to {showingTo} of{" "}
                        {filteredData.length} Lists
                      </span>
                    </div>
                    <nav>
                      <ul className="pagination mt-0">
                        <li
                          className={`page-item ${
                            currentPage === 1 ? "disabled" : ""
                          }`}
                        >
                          <button
                            className="page-link"
                            onClick={() => paginate(currentPage - 1)}
                          >
                            &laquo;
                          </button>
                        </li>
                        {(() => {
                          const pagesToShow = 5;
                          let startPage = Math.max(
                            1,
                            currentPage - Math.floor(pagesToShow / 2),
                          );
                          let endPage = startPage + pagesToShow - 1;
                          if (endPage > totalPage) {
                            endPage = totalPage;
                            startPage = Math.max(1, endPage - pagesToShow + 1);
                          }
                          const pageNumbers = [];
                          for (let i = startPage; i <= endPage; i++) {
                            pageNumbers.push(i);
                          }
                          return pageNumbers.map((pageNum) => (
                            <li
                              key={pageNum}
                              className={`page-item ${
                                currentPage === pageNum ? "active" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() => paginate(pageNum)}
                              >
                                {pageNum}
                              </button>
                            </li>
                          ));
                        })()}
                        <li
                          className={`page-item ${
                            currentPage === totalPage ? "disabled" : ""
                          }`}
                        >
                          <button
                            className="page-link"
                            onClick={() => paginate(currentPage + 1)}
                          >
                            &raquo;
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              </>
            )}
            {isModalOpen && (
              <CustomerDetailModal
                customerDetail={customerDetail}
                setIsModalOpen={setIsModalOpen}
                isEditingCreditLimit={isEditingCreditLimit}
                setIsEditingCreditLimit={setIsEditingCreditLimit}
                isEditingCreditDays={isEditingCreditDays}
                setIsEditingCreditDays={setIsEditingCreditDays}
                creditBal={creditBal}
                setCreditBal={setCreditBal}
                creditLimit={creditLimit}
                setCreditLimit={setCreditLimit}
                handleSaveCreditAndLimit={handleSaveCreditAndLimit}
                clearSaveCreditAndLimit={clearSaveCreditAndLimit}
                formatCustomerId={formatCustomerId}
              />
            )}
          </section>
        </div>
      </AdminHeader>
    </>
  );
};

export default ListDocument;

const CustomDropdown = ({ handleDownloadCSV }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState("Options");
  const dropdownRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);

  const options = [
    { label: "Export", value: "export" },
    { label: "Export All", value: "exportAll" },
  ];
const handleSelect = (opt) => {
  setSelected(opt.label);
  setIsOpen(false);
  
  if (opt.value === "export") {
    // ✅ Export = current page only (but selected overrides)
    handleDownloadCSV("current");
  } else if (opt.value === "exportAll") {
    // ✅ Export All = entire dataset (but selected overrides)
    handleDownloadCSV("all");
  }
  
  setSelected("Options");
};
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isOpen &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isOpen]);
  return (
    <div
      className="custom-select"
      style={{ position: "relative", display: "inline-block" }}
      ref={dropdownRef}
    >
      <div
        className="select-header"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          backgroundColor: isHovered ? "#EDF8FA" : "#fff",
          color: "rgba(129, 134, 134, 1)",
          padding: "8px 12px",
          border: "1px solid #ccc",
          borderRadius: "4px",
          cursor: "pointer",
          width: "140px",
          height: "40px",
          textAlign: "left",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontWeight: 500,
          transition: "background-color 0.2s ease",
        }}
      >
        <span>{selected}</span>
        <ChevronDown />
      </div>
      {isOpen && (
        <ul
          className="select-options"
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            width: "100%",
            listStyle: "none",
            margin: 0,
            padding: 0,
            backgroundColor: "white",
            border: "none",
            borderRadius: "4px",
            zIndex: 1000,
            boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
            fontWeight: 500,
          }}
        >
          {options.map((opt) => (
            <li
              key={opt.value}
              onClick={() => handleSelect(opt)}
              className="select-option"
              style={{
                padding: "8px 12px",
                color: "rgba(129, 134, 134, 1)",
                backgroundColor: "white",
                cursor: "pointer",
                textAlign: "left",
              }}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const FilterPanel = ({ filterValues, onFilterChange, onApply, onClear }) => {
  return (
    <div
      style={{
        position: "absolute",
        top: "100%",
        left: 0,
        width: "500px",
        backgroundColor: "#fff",
        borderRadius: "8px",
        boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
        zIndex: 1000,
        padding: "16px",
        maxHeight: "500px",
        overflowY: "auto",
        marginTop: "10px",
      }}
    >
      <div className="row g-3">
        <div className="col-md-6">
          <select
            className="form-select filter-input"
            value={filterValues.customerType}
            onChange={(e) => onFilterChange("customerType", e.target.value)}
          >
            <option value="">Customer Type</option>
            <option value="dealer">Dealer</option>
            <option value="sub-dealer">Sub Dealer</option>
            <option value="individual">Individual</option>
            <option value="engineer">Engineer</option>
            <option value="builder">Builder</option>
            <option value="architect">Architect</option>
            <option value="contractor">Contractor</option>
          </select>
        </div>
        <div className="col-md-6">
          <select
            className="form-select filter-input"
            value={filterValues.proofStatus}
            onChange={(e) => onFilterChange("proofStatus", e.target.value)}
          >
            <option value="">Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>
      <div className="d-flex gap-14 mt-24">
        <button
          onClick={onApply}
          style={{
            color: "#fff",
            backgroundColor: "#2b95d3",
            borderRadius: "6px",
            fontSize: "14px",
            fontWeight: "700",
            cursor: "pointer",
            padding: "8px 16px",
            minWidth: "150px",
            height: "40px",
            border: "none",
          }}
        >
          Apply Filters
        </button>
        <button
          onClick={onClear}
          style={{
            color: "#2692d2",
            backgroundColor: "#d9ebff",
            borderRadius: "6px",
            fontSize: "14px",
            fontWeight: "700",
            cursor: "pointer",
            padding: "8px 16px",
            minWidth: "150px",
            height: "40px",
            border: "none",
            gap: "6px",
          }}
        >
          Clear All
        </button>
      </div>
    </div>
  );
};

const CustomerDetailModal = ({
  customerDetail,
  setIsModalOpen,
  isEditingCreditLimit,
  isEditingCreditDays,
  setIsEditingCreditLimit,
  setIsEditingCreditDays,
  creditBal,
  setCreditBal,
  creditLimit,
  setCreditLimit,
  handleSaveCreditAndLimit,
  clearSaveCreditAndLimit,
  formatCustomerId
}) => {
  if (!customerDetail) return null;
  return (
    <div
      style={{
        position: "fixed",
        top: -25,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 9999,
      }}
      onClick={() => setIsModalOpen(false)}
    >
      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: "8px",
          padding: "24px",
          width: "68%",
          maxWidth: "95vw",
          maxHeight: "90vh",
          overflowY: "auto",
          position: "relative",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={() => setIsModalOpen(false)}
          style={{
            position: "absolute",
            top: "12px",
            right: "12px",
            width: "32px",
            height: "32px",
            background: "none",
            border: "3px solid #ddd",
            borderRadius: "50%",
            fontSize: "28px",
            cursor: "pointer",
            color: "#6E7C94",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: 0,
          }}
        >
          &times;
        </button>
        <h6
          style={{ marginBottom: "20px", fontWeight: "700", fontSize: "20px" }}
        >
          Customer Details{" "}
          <span
            style={{
              backgroundColor: "#caeadc",
              paddingLeft: "12px",
              paddingRight: "12px",
              textTransform: "capitalize",
              color: "#3c9c73",
              fontWeight: "500",
              fontSize: "15px",
              borderRadius: "15px",
              marginLeft: "20px",
            }}
          >
            {customerDetail.status} User
          </span>
        </h6>
        <div className="row gap-10 mt-24">
          <div className="col-md-2">
            <div className="mb-3">
              <p className="fs-6 fw-bold text-dark">Customer ID</p>
              <p>{formatCustomerId(customerDetail) }</p>
            </div>
          </div>
          <div className="col-md-2">
            <div className="mb-3">
              <p className="fs-6 fw-bold text-dark">Customer Name</p>
              <p>{customerDetail.name}</p>
            </div>
          </div>
          <div className="col-md-2">
            <div className="mb-3">
              <p className="fs-6 fw-bold text-dark">Customer Type</p>
              <p>{customerDetail.customerType}</p>
            </div>
          </div>
          <div className="col-md-2">
            <div className="mb-3">
              <p className="fs-6 fw-bold text-dark">Created At</p>
              <p>{customerDetail.createdAt || "N/A"}</p>
            </div>
          </div>
          <div className="col-md-2">
            <div className="mb-3">
              <p className="fs-6 fw-bold text-dark">Phone Number</p>
              <p>{customerDetail.phoneNumber}</p>
            </div>
          </div>
        </div>
        <div className="row mt-24 gap-10">
          <div className="col-md-2">
            <div className="mb-3">
              <p className="fs-6 fw-bold text-dark">Alternative Phone Number</p>
              <p>{customerDetail.alternativePhoneNumber || "N/A"}</p>
            </div>
          </div>
          <div className="col-md-2">
            <div className="mb-3">
              <p className="fs-6 fw-bold text-dark">Email ID</p>
              <p>{customerDetail.emailId}</p>
            </div>
          </div>
          <div className="col-md-2">
            <div className="mb-3">
              <p className="fs-6 fw-bold text-dark">Alternative Email ID</p>
              <p>{customerDetail.alternativeEmailId || "N/A"}</p>
            </div>
          </div>
          <div className="col-md-2">
            <div className="mb-3">
              <p className="fs-6 fw-bold text-dark">PinCode</p>
              <p>{customerDetail.pincode || "N/A"}</p>
            </div>
          </div>
        </div>
        {customerDetail.proofStatus === 'approved' && (
          <div className="row mt-24">
          <div className="col-md-6">
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <p className="fs-6 fw-bold text-dark">Credit Limit</p>
                {isEditingCreditLimit ? (
                  <div className="d-flex gap-5 ">
                    <button
                      onClick={() => {
                        if (clearSaveCreditAndLimit) clearSaveCreditAndLimit();
                        setIsEditingCreditLimit(true);
                      }}
                      style={{
                        backgroundColor: "#e7f2ff",
                        border: "none",
                        borderRadius: "4px",
                        padding: "4px 12px",
                        fontSize: "14px",
                        cursor: "pointer",
                        color: "#2b95d3",
                        marginBottom: "5px",
                      }}
                    >
                      Clear
                    </button>
                    <button
                      onClick={() => {
                        if (handleSaveCreditAndLimit)
                          handleSaveCreditAndLimit(customerDetail.id);
                        setIsEditingCreditLimit(false);
                      }}
                      style={{
                        backgroundColor: "#2b95d3",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        padding: "4px 12px",
                        fontSize: "14px",
                        cursor: "pointer",
                        marginBottom: "5px",
                      }}
                    >
                      Save
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      setIsEditingCreditLimit(true);
                      setCreditBal(customerDetail.creditBal || "");
                    }}
                    style={{
                      backgroundColor: "#e7f2ff",
                      border: "none",
                      borderRadius: "4px",
                      padding: "4px 12px",
                      fontSize: "14px",
                      cursor: "pointer",
                      color: "#2b95d3",
                      marginBottom: "5px",
                    }}
                  >
                    Edit
                  </button>
                )}
              </div>
              {isEditingCreditLimit ? (
                <input
                  type="text"
                  className="form-control"
                  value={creditBal}
                  onChange={(e) => setCreditBal(e.target.value)}
                  placeholder="Enter credit limit"
                  style={{ flex: 1 }}
                />
              ) : (
                <input
                  type="text"
                  className="form-control"
                  value={
                    customerDetail.creditBal
                      ? customerDetail.creditBal.toLocaleString()
                      : "N/A"
                  }
                  readOnly
                  style={{ backgroundColor: "#F6F8FD" }}
                />
              )}
            </div>
          </div>
          <div className="col-md-6">
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <p className="fs-6 fw-bold text-dark">Credit Days</p>
                {isEditingCreditDays ? (
                  <div className="d-flex gap-5">
                    <button
                      onClick={() => {
                        if (clearSaveCreditAndLimit) clearSaveCreditAndLimit();
                        setIsEditingCreditDays(true);
                      }}
                      style={{
                        backgroundColor: "#e7f2ff",
                        border: "none",
                        borderRadius: "4px",
                        padding: "4px 12px",
                        fontSize: "14px",
                        cursor: "pointer",
                        color: "#2b95d3",
                        marginBottom: "5px",
                      }}
                    >
                      Clear
                    </button>
                    <button
                      onClick={() => {
                        if (handleSaveCreditAndLimit)
                          handleSaveCreditAndLimit(customerDetail.id);
                        setIsEditingCreditDays(false);
                      }}
                      style={{
                        backgroundColor: "#2b95d3",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        padding: "4px 12px",
                        fontSize: "14px",
                        cursor: "pointer",
                        marginBottom: "5px",
                      }}
                    >
                      Save
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      setIsEditingCreditDays(true);
                      setCreditLimit(customerDetail.creditLimit || "");
                    }}
                    style={{
                      backgroundColor: "#e7f2ff",
                      border: "none",
                      borderRadius: "4px",
                      padding: "4px 12px",
                      fontSize: "14px",
                      cursor: "pointer",
                      color: "#2b95d3",
                      marginBottom: "5px",
                    }}
                  >
                    Edit
                  </button>
                )}
              </div>
              {isEditingCreditDays ? (
                <input
                  type="text"
                  className="form-control"
                  value={creditLimit}
                  onChange={(e) => setCreditLimit(e.target.value)}
                  placeholder="Enter credit days"
                  style={{ flex: 1 }}
                />
              ) : (
                <input
                  type="text"
                  className="form-control"
                  value={customerDetail.creditLimit || "N/A"}
                  readOnly
                  style={{ backgroundColor: "#F6F8FD" }}
                />
              )}
            </div>
          </div>
        </div>
        )}
        
        <div className="row mt-24">
          <p className="fs-5 fw-bold text-dark mb-10 mt-24">
            Attached Documents
          </p>
          {customerDetail?.proof && customerDetail.proof.length > 0 ? (
            customerDetail.proof.map((doc, index) => (
              <div key={index} className="col-md-4 mb-10">
                <div className="p-3  py-14 d-flex ">
                  <div className="me-16 document-pdf-background">
                    <img
                      src="/assets/images/doc-pdf.png"
                      alt="View"
                      style={{
                        width: "35px",
                        height: "35px",
                        objectFit: "contain",
                      }}
                    />
                  </div>
                  <div className="flex-grow-1">
                    <p className="fs-6 fw-bold mb-1 text-dark">
                      {doc.proofName || "N/A"}
                    </p>
                    <a
                      href={baseURL + "/" + doc.proofAttachment}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => {
                        e.preventDefault();
                        window.open(
                          baseURL + "/" + doc.proofAttachment,
                          "_blank"
                        );
                      }}
                      className="text-decoration-none fs-6"
                      style={{ color: "#2592D2", fontWeight: "500" }}
                    >
                      View
                    </a>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="col-12">
              <p className="text-muted fs-6">No documents attached.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const getStatusColors = (status) => {
  switch (status) {
    case "approved":
      return { color: "#46A27B", bg: "#D5F5E7", border: "none" };
    case "pending":
      return { color: "#CF9500", bg: "#FFF3BA", border: "none" };
    case "rejected":
      return { color: "#E54611", bg: "#FDDFD0", border: "none" };
    default:
      return { color: "black", bg: "#f5f5f5" };
  }
};
